import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { GenericService } from '../../generic.service';
import { FormsModule } from '@angular/forms';
import { LandingService } from '../../landing-page/service/landing.service';
import { HeaderComponent } from '../../shared/header/header.component';
import { Subscription } from 'rxjs';
import { defaultLanguage } from '../../shared/constants/language.constants';
import { CrowdinService } from '../../shared/service/crowdin.service';
import { MachineIssueListItem } from '../interface/troubleshoot-eh500s.interface';
import { TroubleShootEh500sService } from '../service/trouble-shoot-eh500s.service';

@Component({
    selector: 'app-steps-eh500s',
    imports: [CommonModule, RouterModule, FormsModule, HeaderComponent],
    templateUrl: './steps-eh500s.component.html',
    styleUrls: ['./steps-eh500s.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default
})
export class StepsEh500sComponent {
  stepId: string | null = '';
  step: any;
  stepsList: any;
  currentStepIndex: number = 0;
  issueName: string | undefined;
  deviceData: any = {};
  deviceData$: any = null;

  machineIssueList: MachineIssueListItem[] = [];
  accountNumber: string = '';
  languageDetails!: (item: any) => any;

  currentQuestion: MachineIssueListItem | null = null;
  languageIndex: number = 0;
  translations: { [key: string]: string } = {};
  currentLanguage: string = defaultLanguage;
  languageOptions: Array<{ value: string }> = [];
  isLoadingTranslations = true;
  subs = new Subscription();

  constructor(
    private troubleShootService: TroubleShootEh500sService,
    private route: ActivatedRoute,
    private router: Router,
    private genericService: GenericService,
    private crowdinService: CrowdinService,
    private landingService: LandingService,
  ) {
    this.deviceData$ = this.genericService.deviceData.subscribe((data) => {
      if (data) {
        this.deviceData = data;
      }
    });
  }

  ngOnInit() {
    this.languageOptions = this.landingService.getLanguageOptions();

    const stored = localStorage.getItem('deviceData');
    const selectedName = stored
      ? JSON.parse(stored).languageSelected || defaultLanguage
      : defaultLanguage;
    this.currentLanguage = selectedName;

    this.subs.add(
      this.landingService.languageName$.subscribe((langName) => {
        this.currentLanguage = langName;
        this.languageIndex =
          this.crowdinService.getLanguageIndexByName(langName);
        this.translations = this.crowdinService.getAllTranslations();
      }),
    );

    this.landingService.loadTranslations(this.currentLanguage);
    this.stepId = this.route.snapshot.paramMap.get('stepId');

    if (this.stepId) {
      this.step = this.troubleShootService.getTroubleShootStep(this.stepId);

      if (this.step) {
        this.issueName = this.troubleShootService.getIssuename();
      } else {
        console.error(`No step found for ID: ${this.stepId}`);
      }

      this.stepsList = this.troubleShootService.getStepState();

      if (this.stepsList && this.stepsList.length) {
        this.currentStepIndex = this.troubleShootService.getStepIndex(
          this.stepId,
        );
      }

      this.loadMachineIssuelist();
    }
  }

  async languageChange(event: any) {
    const selectedLang = event.target.value;
    await this.landingService.loadTranslations(selectedLang);
  }

  resetIssueList() {
    this.machineIssueList.forEach((question) => {
      question.status = undefined;
      question.answered = false;
      question.expanded = false;
    });
  }

  loadMachineIssuelist() {
    if (
      !this.step ||
      !this.step.machineIssueListItems ||
      this.step.machineIssueListItems.length === 0
    ) {
      return;
    }

    this.machineIssueList = [...this.step.machineIssueListItems];
    this.resetIssueList();
  }

  selection(option: any) {
    this.resetIssueList();
    this.machineIssueList = [];
    if (option) {
      this.router.routeReuseStrategy.shouldReuseRoute = () => false;
      this.router.onSameUrlNavigation = 'reload';
      const originalUrl: string = this.router.url;
      const parts: string[] = originalUrl.split('/');
      const lastPart: string | undefined = parts.pop();
      if (lastPart) {
        const replacedUrl: string = originalUrl.replace(
          lastPart,
          option.steps[0].key,
        );
        this.router.navigateByUrl(replacedUrl).then(() => {
          this.step = this.troubleShootService.getTroubleShootStep(
            option.steps[0].key,
          );
          const issueListStep = this.step.steps?.find(
            (s: { key: string }) => s.key === 'eh500s-issueList-items',
          );

          if (issueListStep && issueListStep.machineIssueListItems) {
            this.machineIssueList = issueListStep.machineIssueListItems;
          } else {
            this.machineIssueList = [];
          }
        });
      }
    }
  }

  next() {
    this.currentStepIndex += 1;

    if (this.currentStepIndex < this.stepsList.length) {
      const nextStep = this.stepsList[this.currentStepIndex];
      if (nextStep?.key) {
        this.resetIssueList();
        this.router.navigate([nextStep.key]);
        this.loadMachineIssueListForStep(nextStep);
      }
    }
  }

  loadMachineIssueListForStep(step: any) {
    const issueListStep = step.steps?.find(
      (s: { key: string }) => s.key === 'eh500s-issueList-items',
    );
    this.machineIssueList = issueListStep
      ? issueListStep.machineIssueListItems
      : [];
    this.resetIssueList();
  }

  answer(index: number, status: string): void {
    const item = this.machineIssueList[index];
    item.status = status;
    item.answered = true;
    item.expanded = !!item.detailKeys?.length;
  }

  translate(key: string): string {
    return this.translations[key] || key;
  }

  toggleAccordion(index: number) {
    this.machineIssueList[index].expanded =
      !this.machineIssueList[index].expanded;
  }

  resolved() {
    this.deviceData['languageSelected'] = this.currentLanguage;
    this.genericService.setLocalStorage(this.deviceData);
    this.genericService.deviceData.next(this.deviceData);
    this.router.navigateByUrl('thankYou');
  }

  needHelp() {
    this.landingService.needHelp();
  }

  get progress() {
    const answered = this.machineIssueList.filter(
      (item) => item.answered,
    ).length;
    return (answered / this.machineIssueList.length) * 100;
  }
}
